import {
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../lib/authErrors";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

export const UPLOAD_TIMEOUT_MS = 90_000;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ALLOWED_EXTENSIONS = [".pdf", ".txt", ".docx"] as const;

export type KbDocumentStatus = "uploaded" | "processing" | "ready" | "failed";
export type KbDocumentSourceType = "file" | "url";

export type KbDocument = {
  id: string;
  filename: string;
  status: KbDocumentStatus;
  error_text: string | null;
  entries_created: number;
  created_at: string;
  source_type?: KbDocumentSourceType | null;
};

export type KbImportResult = {
  kind: "success" | "error";
  message: string;
};

export const KB_URL_ZERO_ENTRIES_MESSAGE =
  "We couldn't read enough content from this site. Some websites can't be imported automatically — try uploading a document instead.";

export const KB_DOCUMENT_ZERO_ENTRIES_MESSAGE =
  "We couldn't read enough content from this document. Try a different file or add articles manually.";

function getCustomerId(): string | null {
  return localStorage.getItem("customerId");
}

export function normalizeImportUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function parseApiError(data: Record<string, unknown>): string {
  const det =
    (typeof data?.error === "string" ? data.error : null) ||
    (typeof data?.message === "string" ? data.message : null) ||
    (Array.isArray(data?.details) ? (data.details as string[]).join("; ") : "");
  return det;
}

export function parseUrlImportError(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const nested = record.error;
  if (nested && typeof nested === "object") {
    const message = (nested as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  }
  const flat = parseApiError(record);
  return flat || null;
}

function hasExtractableEntries(doc: KbDocument | undefined): boolean {
  if (!doc) return false;
  if (doc.status === "failed") return false;
  return doc.entries_created > 0;
}

function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export function validateUploadFile(file: File): string | null {
  const ext = extensionOf(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
    return "Please upload a PDF, DOCX, or TXT file.";
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return "File must be 10MB or smaller.";
  }
  if (file.size <= 0) {
    return "File is empty.";
  }
  return null;
}

export function mapRawDocuments(raw: unknown[]): KbDocument[] {
  return raw.map((d) => {
    const doc = d as Record<string, unknown>;
    return {
      id: String(doc.id),
      filename: String(doc.filename ?? "Document"),
      status: (doc.status ?? "uploaded") as KbDocumentStatus,
      error_text: (doc.error_text as string | null) ?? null,
      entries_created: Number(doc.entries_created ?? 0),
      created_at: String(doc.created_at ?? ""),
      source_type: (doc.source_type ?? "file") as KbDocumentSourceType,
    };
  });
}

export function formatKbImportSuccessMessage(doc: KbDocument): string {
  return `${doc.entries_created} draft entries created — review and approve them in your Knowledge Base after setup.`;
}

export function formatKbUploadSuccessMessage(doc: KbDocument): string {
  return `${doc.entries_created} entries drafted from ${doc.filename} — review them below`;
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

type UseKbImportOptions = {
  onDocumentsChanged?: () => void | Promise<void | boolean>;
  onEntriesChanged?: () => void | Promise<void | boolean>;
};

export function useKbImport(options: UseKbImportOptions = {}) {
  const { onDocumentsChanged, onEntriesChanged } = options;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlValue, setUrlValue] = useState("");
  const [urlSubmitting, setUrlSubmitting] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [urlImportSuccess, setUrlImportSuccess] = useState<KbImportResult | null>(
    null,
  );

  const [uploading, setUploading] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadInlineError, setUploadInlineError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<KbImportResult | null>(null);

  const refreshAfterImport = useCallback(async () => {
    await onDocumentsChanged?.();
    await onEntriesChanged?.();
  }, [onDocumentsChanged, onEntriesChanged]);

  const importFromUrl = useCallback(async (): Promise<KbImportResult | null> => {
    const normalizedUrl = normalizeImportUrl(urlValue);
    if (!normalizedUrl) {
      setUrlError("Enter a website URL.");
      return null;
    }

    const customerId = getCustomerId();
    if (!customerId) {
      setUrlError("No customer ID found.");
      return null;
    }

    setUrlError(null);
    setUrlImportSuccess(null);
    setUrlSubmitting(true);

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/kb/documents/from-url`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: normalizedUrl }),
        },
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          setUrlError("Your session expired. Please log in again.");
        });
        return null;
      }
      if (res.status === 201) {
        setUrlValue("");
        const data = await res.json().catch(() => ({}));
        const doc = mapRawDocuments([data])[0];
        await refreshAfterImport();

        if (doc?.status === "failed") {
          const message =
            doc.error_text?.trim() || "Couldn't import that site — try again.";
          setUrlImportSuccess(null);
          setUrlError(message);
          return { kind: "error", message };
        }

        if (!hasExtractableEntries(doc)) {
          setUrlImportSuccess(null);
          setUrlError(KB_URL_ZERO_ENTRIES_MESSAGE);
          return { kind: "error", message: KB_URL_ZERO_ENTRIES_MESSAGE };
        }

        setUrlError(null);
        const result: KbImportResult = {
          kind: "success",
          message: formatKbImportSuccessMessage(doc),
        };
        setUrlImportSuccess(result);
        return result;
      }
      if (res.status === 400 || res.status === 422) {
        const data = await res.json().catch(() => ({}));
        const message =
          parseUrlImportError(data) || "Couldn't import that site — try again.";
        setUrlImportSuccess(null);
        setUrlError(message);
        return { kind: "error", message };
      }
      setUrlError("Couldn't import that site — try again.");
      return null;
    } catch {
      setUrlError("Couldn't import that site — try again.");
      return null;
    } finally {
      setUrlSubmitting(false);
    }
  }, [refreshAfterImport, urlValue]);

  const onUrlInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !urlSubmitting) {
        e.preventDefault();
        void importFromUrl();
      }
    },
    [importFromUrl, urlSubmitting],
  );

  const uploadDocument = useCallback(
    async (file: File): Promise<KbImportResult | null> => {
      const validationError = validateUploadFile(file);
      if (validationError) {
        setUploadInlineError(validationError);
        return null;
      }

      const customerId = getCustomerId();
      if (!customerId) {
        setUploadInlineError("No customer ID found.");
        return null;
      }

      setUploadInlineError(null);
      setUploadResult(null);
      setUploading(true);

      const form = new FormData();
      form.append("file", file);

      try {
        const res = await fetchWithTimeout(
          `${API_BASE}/customers/${customerId}/kb/documents`,
          {
            method: "POST",
            credentials: "include",
            body: form,
          },
          UPLOAD_TIMEOUT_MS,
        );
        if (isAuthHttpStatus(res.status)) {
          void handleAuthHttpStatus(res.status, () => {
            setUploadResult({
              kind: "error",
              message: "Your session expired. Please log in again.",
            });
          });
          return null;
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg =
            parseApiError(data as Record<string, unknown>) ||
            `Upload failed (HTTP ${res.status}).`;
          const result = { kind: "error" as const, message: msg };
          setUploadResult(result);
          return result;
        }

        const doc = mapRawDocuments([data])[0];
        await refreshAfterImport();

        if (doc.status === "failed") {
          const result = {
            kind: "error" as const,
            message: doc.error_text || "Document processing failed.",
          };
          setUploadResult(result);
          return result;
        }

        if (!hasExtractableEntries(doc)) {
          const result = {
            kind: "error" as const,
            message: KB_DOCUMENT_ZERO_ENTRIES_MESSAGE,
          };
          setUploadResult(result);
          return result;
        }

        const result = {
          kind: "success" as const,
          message: formatKbUploadSuccessMessage(doc),
        };
        setUploadResult(result);
        return result;
      } catch (err: unknown) {
        const aborted =
          err instanceof DOMException && err.name === "AbortError";
        const result = {
          kind: "error" as const,
          message: aborted
            ? "Upload timed out. Try a smaller document or try again."
            : err instanceof Error
              ? err.message
              : "Upload failed.",
        };
        setUploadResult(result);
        return result;
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [refreshAfterImport],
  );

  const onFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void uploadDocument(file);
    },
    [uploadDocument],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setUploadDragOver(false);
      if (uploading) return;
      const file = e.dataTransfer.files?.[0];
      if (file) void uploadDocument(file);
    },
    [uploadDocument, uploading],
  );

  return {
    fileInputRef,
    urlValue,
    setUrlValue,
    urlSubmitting,
    urlError,
    setUrlError,
    urlImportSuccess,
    setUrlImportSuccess,
    importFromUrl,
    onUrlInputKeyDown,
    uploading,
    uploadDragOver,
    setUploadDragOver,
    uploadInlineError,
    uploadResult,
    setUploadResult,
    uploadDocument,
    onFileInputChange,
    onDrop,
  };
}
